import { languageInfo } from 'config/constants';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { memo } from 'react';
import CountryFlag from 'components/shared/CountryFlag';

const getFullLanguageCode = shortCode => {
  if (!shortCode) return '';
  return Object.keys(languageInfo).find(code => code.startsWith(shortCode.toLowerCase())) || shortCode;
};

const getLanguageInfo = langCode => {
  const fullCode = getFullLanguageCode(langCode);
  return languageInfo[fullCode] || null;
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    overflow: 'hidden'
  },
  nameWrapper: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flex: '1 1 auto'
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flex: '1 1 auto'
  },
  nameText: {
    color: 'rgba(0, 0, 0, 0.87)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: '1 1 auto'
  },
  languageFlag: {
    fontSize: '14px',
    color: 'rgba(0, 0, 0, 0.6)',
    padding: '2px 6px',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '8px',
    flexShrink: 0 // Prevent flag from shrinking
  },
  separator: {
    margin: '0 8px',
    color: 'rgba(0, 0, 0, 0.38)',
    flexShrink: 0 // Prevent separator from shrinking
  },
  singleLanguageFlag: {
    opacity: 0,
    '&:hover, &:focus-within': {
      opacity: 1
    },
    transition: 'opacity 0.2s ease-in-out'
  }
}));

const BoardNameCell = ({ names }) => {
  const classes = useStyles();

  if (!names || typeof names !== 'object') {
    return (
      <Typography variant="body2" color="textSecondary">
        -
      </Typography>
    );
  }

  const namesArray = Object.entries(names);
  if (!namesArray.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        -
      </Typography>
    );
  }

  const hasMultipleLanguages = namesArray.length > 1;
  const allSameName = namesArray.every(([_, name]) => name === namesArray[0][1]);

  const getLanguageDisplay = langCode => {
    const info = getLanguageInfo(langCode);
    return info ? <CountryFlag svgFlag={info.svgFlag} /> : langCode.toUpperCase();
  };

  const renderLanguageFlag = langCode => {
    const info = getLanguageInfo(langCode);

    if (!info) {
      return <span className={classes.languageFlag}>{`(${langCode})`}</span>;
    }

    return (
      <span className={classes.languageFlag} role="img" aria-label={`Language: ${info.initial}`}>
        <CountryFlag svgFlag={info?.svgFlag} />
      </span>
    );
  };

  return (
    <div className={classes.container}>
      {namesArray.map(([lang, name], index) => (
        <span key={lang} className={classes.nameWrapper}>
          <Tooltip
            title={
              <div>
                {namesArray.map(([l, n]) => (
                  <div key={l} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {getLanguageDisplay(l)} {n}
                  </div>
                ))}
              </div>
            }
            arrow
            placement="top"
            enterDelay={500}
          >
            <div
              className={classes.nameContainer}
              tabIndex={0}
              role="button"
              aria-label={`${name} (${getLanguageDisplay(lang)})`}
            >
              <Typography component="span" variant="body2" className={classes.nameText}>
                {name}
              </Typography>
              {hasMultipleLanguages && renderLanguageFlag(lang)}
            </div>
          </Tooltip>
          {index < namesArray.length - 1 && !allSameName && (
            <span className={classes.separator} role="separator" aria-hidden="true">
              |
            </span>
          )}
        </span>
      ))}
    </div>
  );
};

export default memo(BoardNameCell);
